import {
  domIdFor,
  formatLabel,
  sectionKeyFor
} from '../../library/attributes-section/util';

export type SearchEntry = {
  key: string;
  label: string;
  sectionTitle: string;
  headingKey: string;
  anchorId: string;
  domId: string;
  name: string;
  description: string;
  section: string;
  options: string;
};

export type SearchResults = {
  results: SearchEntry[];
  total: number;
};

export const RESULT_LIMIT = 20;

// Field weights, highest first — a name match should always outrank a description match
const NAME_WEIGHT = 4;
const SECTION_WEIGHT = 3;
const DESCRIPTION_WEIGHT = 2;
const OPTION_WEIGHT = 1;
const PREFIX_BONUS = 10;

// Separators fold to spaces so 'pull through' matches pull_through_sites
export function normalize(text: any): string {
  if (text === null || text === undefined) return '';

  return String(text)
    .toLowerCase()
    .replace(/[-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Matches what the page actually renders. formatLabel drops words such as 'available'
// and can empty out a short name entirely, so the raw name is the fallback
function displayLabel(attribute: any): string {
  if (!attribute.name) return attribute.id;

  return formatLabel(attribute.name, attribute.depends_on) || attribute.name;
}

function optionNames(attribute: any): string {
  if (!attribute || !attribute.options) return '';

  return attribute.options
    .map((option) => (option && (option.name || option.label || option.id)) || '')
    .join(' ');
}

export function buildSearchIndex(sections: any[] = []): SearchEntry[] {
  const index: SearchEntry[] = [];

  sections.forEach((section) => {
    if (!section || !section.attributes) return;

    const sectionTitle = section.sectionTitle || '';
    const sectionKey = sectionKeyFor(sectionTitle);
    const normalizedSection = normalize(sectionTitle);
    const siblingIds = section.attributes.map((attribute) => attribute && attribute.id);

    section.attributes.forEach((attribute) => {
      if (!attribute || !attribute.id) return;

      const parentId = attribute.depends_on;
      const targetId =
        parentId && siblingIds.indexOf(parentId) > -1 ? parentId : attribute.id;

      const label = displayLabel(attribute);

      index.push({
        key: `${sectionKey}:${attribute.id}`,
        label,
        sectionTitle,
        headingKey: section.parent,
        anchorId: sectionKey,
        domId: domIdFor(sectionTitle, targetId),
        // Both wordings stay searchable: the config name and the shortened label on screen
        name: normalize(`${attribute.name || ''} ${label}`),
        description: normalize(attribute.description),
        section: normalizedSection,
        options: normalize(optionNames(attribute))
      });
    });
  });

  return index;
}

// Highest weight among the fields containing the term, or 0 when the term is absent
function termWeight(entry: SearchEntry, term: string): number {
  if (entry.name.indexOf(term) > -1) return NAME_WEIGHT;
  if (entry.section.indexOf(term) > -1) return SECTION_WEIGHT;
  if (entry.description.indexOf(term) > -1) return DESCRIPTION_WEIGHT;
  if (entry.options.indexOf(term) > -1) return OPTION_WEIGHT;

  return 0;
}

function scoreEntry(entry: SearchEntry, terms: string[], query: string): number {
  let score = 0;

  for (const term of terms) {
    const weight = termWeight(entry, term);
    // Every term has to land somewhere, so an extra word narrows rather than widens
    if (weight === 0) return 0;
    score += weight;
  }

  if (entry.name.indexOf(query) === 0) score += PREFIX_BONUS;

  return score;
}

export function searchAttributes(
  index: SearchEntry[] = [],
  query: string,
  limit: number = RESULT_LIMIT
): SearchResults {
  const normalizedQuery = normalize(query);
  const terms = normalizedQuery.split(' ').filter(Boolean);

  if (terms.length === 0) return { results: [], total: 0 };

  const scored = [];

  index.forEach((entry) => {
    const score = scoreEntry(entry, terms, normalizedQuery);
    if (score > 0) scored.push({ entry, score });
  });

  // Label breaks ties so results keep a stable order between keystrokes
  scored.sort((a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label));

  return {
    results: scored.slice(0, limit).map((match) => match.entry),
    total: scored.length
  };
}
